import { ReactNode } from 'react'
import { cn } from 'lib/utils'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'

export type LocationGroupCardLocation = {
	_id: string
	image?: string | null
	assetName: string
}

export type LocationGroupCardProps = {
	location: LocationGroupCardLocation
	/** Optional count rendered as a small badge in the header. */
	itemCount?: number
	children: ReactNode
	className?: string
}

/**
 * Container card for grouping items belonging to a single location, used by
 * the rebuilt Portfolio Notes / Tasks / Images / Documents subpages. Shows a
 * left-justified header with the location's avatar + name (and an optional
 * item-count badge), then renders `children` below.
 */
export function LocationGroupCard({
	location,
	itemCount,
	children,
	className,
}: LocationGroupCardProps) {
	return (
		<div
			className={cn(
				'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden',
				className,
			)}
		>
			<div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
				<Avatar className="h-8 w-8 flex-shrink-0">
					<AvatarImage src={location.image ?? undefined} alt={location.assetName} />
					<AvatarFallback className="text-xs">
						{location.assetName?.charAt(0)?.toUpperCase() || 'L'}
					</AvatarFallback>
				</Avatar>
				<span className="text-sm font-semibold text-gray-900 truncate min-w-0">
					{location.assetName}
				</span>
				{itemCount != null && (
					<Badge variant="secondary" className="ml-auto text-[10px] flex-shrink-0">
						{itemCount}
					</Badge>
				)}
			</div>
			<div className="p-4">{children}</div>
		</div>
	)
}

export default LocationGroupCard
